import { Component, type ReactNode } from "react";
import { captureError } from "@/lib/sentry";
import { recoverFromChunkLoadError } from "@/lib/chunkLoadRecovery";

interface Props {
  children: ReactNode;
  fallback?: ReactNode | ((reset: () => void) => ReactNode);
  section?: string;
  resetKeys?: unknown[];
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
    this.resetErrorBoundary = this.resetErrorBoundary.bind(this);
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (recoverFromChunkLoadError(error)) {
      return;
    }

    const extra: Record<string, unknown> = {
      ...(errorInfo as unknown as Record<string, unknown>),
    };
    if (this.props.section) {
      extra.section = this.props.section;
    }
    captureError(error, extra);
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.state.hasError || !this.props.resetKeys) return;
    const prev = prevProps.resetKeys ?? [];
    const curr = this.props.resetKeys;
    if (
      prev.length !== curr.length ||
      prev.some((v, i) => v !== curr[i])
    ) {
      this.resetErrorBoundary();
    }
  }

  resetErrorBoundary() {
    this.setState({ hasError: false });
  }

  render() {
    if (this.state.hasError) {
      if (typeof this.props.fallback === "function") {
        return this.props.fallback(this.resetErrorBoundary);
      }
      return (
        this.props.fallback ?? (
          <div className="p-8 text-center flex flex-col items-center justify-center min-h-[400px]">
            <h1 className="text-payfit-h1 mb-2 text-foreground">Une erreur inattendue est survenue</h1>
            <p className="text-muted-foreground mb-6">Notre équipe a été notifiée. Veuillez rafraîchir la page.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary text-payfit-on-dark rounded-md hover:bg-primary/90 transition-colors"
            >
              Rafraîchir
            </button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
